<template>
  <div class="bbm-topbar">
    <nav class="bbm-nav">
      <div class="bbm-nav__inner">
        <span class="bbm-nav__logo cursor-pointer" @click="$router.push('/dashboards/home')">
          <img src="/images/static-home-page/brussels/brussels-innovation-ecosystem.png" class="w-36"/>
        </span>

        <!-- Desktop links -->
        <div class="bbm-nav__links">
          <span
            v-for="item in navItems"
            :key="item.key"
            :class="['bbm-nav__link cursor-pointer', currentPage === item.key ? 'active' : '']"
            @click="$router.push(item.path)"
          >{{ item.label }}</span>
        </div>

        <!-- Right side -->
        <div class="bbm-nav__actions tw-flex items-center gap-3">
          <template v-if="!$store.getters.isLoggedIn">
            <span class="bbm-nav__login bbm-desktop-only cursor-pointer" @click="$router.push('/login')">Login</span>
            <span class="bbm-nav__cta bbm-desktop-only cursor-pointer" @click="$router.push('/register')">Join Ecosystem</span>
          </template>
          <div @click="openFeedbackWindow" class="bbm-desktop-only cursor-pointer">
            <Icon name="information-outline" class="mt-1"/>
          </div>
          <!-- Hamburger (mobile only) -->
          <button class="bbm-hamburger" @click="menuOpen = !menuOpen" aria-label="Menu">
            <span class="bbm-hamburger__bar" :class="{ open: menuOpen }"></span>
            <span class="bbm-hamburger__bar" :class="{ open: menuOpen }"></span>
            <span class="bbm-hamburger__bar" :class="{ open: menuOpen }"></span>
          </button>
        </div>
      </div>
    </nav>

    <!-- Mobile drawer -->
    <transition name="drawer">
      <div v-if="menuOpen" class="bbm-drawer">
        <div
          v-for="item in navItems"
          :key="item.key"
          :class="['bbm-drawer__item', currentPage === item.key ? 'active' : '']"
          @click="$router.push(item.path); menuOpen = false"
        >{{ item.label }}</div>
        <div class="bbm-drawer__divider" v-if="!$store.getters.isLoggedIn"></div>
        <template v-if="!$store.getters.isLoggedIn">
          <div class="bbm-drawer__item" @click="$router.push('/login'); menuOpen = false">Login</div>
          <div class="bbm-drawer__cta" @click="$router.push('/register'); menuOpen = false">Join Ecosystem</div>
        </template>
      </div>
    </transition>
  </div>
</template>

<script lang="ts">
  import { defineComponent } from 'vue'
  import { openFeedbackWidget } from '@/util/feedback-widget'
  import { trackHeapEvent } from '@/util/analytics'

  export default defineComponent({
    name: 'TopBarStaticBrussels',
    data() {
      return {
        menuOpen: false,
      }
    },
    computed: {
      navItems() {
        return [
          { key: 'home',      label: 'Ecosystem',  path: '/dashboards/home' },
          { key: 'directory', label: 'Directory',   path: '/directory-brussels' },
          { key: 'journeys',  label: 'Journeys',    path: '/journeys-brussels' },
          { key: 'hubs',      label: 'Hubs',        path: '/hubs-overview-brussels' },
          { key: 'events',    label: 'Events',      path: '/events-brussels' },
          { key: 'about',     label: 'About',       path: '/about-brussels' },
        ]
      },
      currentPage() {
        const path = this.$route.path
        if (path.startsWith('/directory-brussels')) return 'directory'
        if (path.startsWith('/journeys-brussels'))   return 'journeys'
        if (path.startsWith('/hubs-overview-brussels') || path.startsWith('/hubs-brussels')) return 'hubs'
        if (path.startsWith('/events-brussels'))     return 'events'
        if (path.startsWith('/insights-brussels'))   return 'insights'
        if (path.startsWith('/about-brussels'))      return 'about'
        return 'home'
      },
    },
    watch: {
      '$route.path'() {
        this.menuOpen = false
      },
    },
    methods: {
      trackHeapEvent,
      openFeedbackWindow() {
        openFeedbackWidget()
      },
    },
  })
</script>

<style scoped lang="scss">
  $bbm-primary: #001e40;
  $bbm-accent:  #ff6f31;

  .bbm-topbar {
    font-family: 'Space Grotesk', 'Inter', sans-serif;
    position: sticky;
    top: 0;
    z-index: 50;
  }

  .bbm-nav {
    background: rgba(255, 255, 255, 0.92);
    backdrop-filter: blur(12px);
    border-bottom: 1px solid #e2e8f0;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  }

  .bbm-nav__inner {
    display: flex;
    align-items: center;
    gap: 16px;
    width: 100%;
    max-width: 1280px;
    margin: 0 auto;
    padding: 16px;

    @media (min-width: 768px) {
      padding: 16px 32px;
      gap: 24px;
    }
  }

  .bbm-nav__logo {
    flex-shrink: 0;
    &:hover { opacity: 0.85; }
  }

  /* Desktop nav links — hidden on mobile */
  .bbm-nav__links {
    display: none;

    @media (min-width: 768px) {
      display: flex;
      align-items: center;
      gap: 28px;
      flex: 1;
    }
  }

  .bbm-nav__actions {
    margin-left: auto;
    flex-shrink: 0;
  }

  .bbm-desktop-only {
    display: none;
    @media (min-width: 768px) { display: inline-flex; }
  }

  .bbm-nav__link {
    font-size: 0.95rem;
    font-weight: 500;
    color: #475569;
    padding-bottom: 4px;
    border-bottom: 2px solid transparent;
    transition: color 0.15s, border-color 0.15s;
    white-space: nowrap;

    &:hover { color: $bbm-primary; }
    &.active { color: $bbm-primary; border-bottom-color: $bbm-primary; }
  }

  .bbm-nav__login {
    padding: 8px 16px;
    font-weight: 500;
    font-size: 0.9rem;
    color: #475569;
    border-radius: 4px;
    transition: background 0.15s;
    &:hover { background: #f1f5f9; color: $bbm-primary; }
  }

  .bbm-nav__cta {
    padding: 10px 20px;
    background: $bbm-accent;
    color: white;
    font-weight: 700;
    font-size: 0.9rem;
    transition: opacity 0.15s, transform 0.15s;
    &:hover { opacity: 0.9; transform: scale(1.03); }
  }

  /* Hamburger */
  .bbm-hamburger {
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 5px;
    width: 36px;
    height: 36px;
    padding: 6px;
    background: none;
    border: none;
    cursor: pointer;

    @media (min-width: 768px) {
      display: none;
    }
  }

  .bbm-hamburger__bar {
    display: block;
    width: 100%;
    height: 2px;
    background: $bbm-primary;
    border-radius: 2px;
    transition: transform 0.2s, opacity 0.2s;

    &:nth-child(1).open { transform: translateY(7px) rotate(45deg); }
    &:nth-child(2).open { opacity: 0; }
    &:nth-child(3).open { transform: translateY(-7px) rotate(-45deg); }
  }

  /* Mobile drawer */
  .bbm-drawer {
    background: white;
    border-bottom: 1px solid #e2e8f0;
    padding: 8px 0 16px;

    @media (min-width: 768px) {
      display: none;
    }
  }

  .bbm-drawer__item {
    padding: 14px 20px;
    font-size: 1rem;
    font-weight: 500;
    color: #475569;
    cursor: pointer;
    transition: background 0.15s, color 0.15s;

    &:hover { background: #f8fafc; color: $bbm-primary; }
    &.active { color: $bbm-primary; font-weight: 600; }
  }

  .bbm-drawer__divider {
    height: 1px;
    background: #e2e8f0;
    margin: 8px 0;
  }

  .bbm-drawer__cta {
    margin: 12px 20px 0;
    padding: 12px;
    background: $bbm-accent;
    color: white;
    font-weight: 700;
    font-size: 0.95rem;
    text-align: center;
    cursor: pointer;
    border-radius: 4px;
    transition: opacity 0.15s;
    &:hover { opacity: 0.9; }
  }

  /* Drawer slide-down transition */
  .drawer-enter-active, .drawer-leave-active {
    transition: max-height 0.25s ease, opacity 0.2s ease;
    max-height: 400px;
    overflow: hidden;
  }
  .drawer-enter-from, .drawer-leave-to {
    max-height: 0;
    opacity: 0;
  }
</style>
